import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export type ApiRequest_Get_Group_Negotiation_Members = {
  communityId: number;
  negotiationId: number;
};

export type ApiResponse_Get_Group_Negotiation_Members = Array<{
  id: number;
  user_id: string;
  full_name: string;
  email: string;
  is_verified: boolean;
  role: string;
  joined_at: string;
}>;

const METHOD = 'GET';
const ENDPOINT = 'communities/{communityId}/group-negotiations/{negotiationId}/members';

export const QUERY_KEY = (params: ApiRequest_Get_Group_Negotiation_Members) =>
  getQueryKey(
    METHOD,
    getResolvedEndpoint(ENDPOINT, { 
      communityId: params.communityId.toString(),
      negotiationId: params.negotiationId.toString()
    }),
    {}
  );

export const useGetGroupNegotiationMembers = (
  params: ApiRequest_Get_Group_Negotiation_Members,
  enabled = true
) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token && !!params.communityId && !!params.negotiationId,
    queryKey: QUERY_KEY(params),
    queryFn: async () => {
      try {
        const data = await handleApiCall<
          ApiResponse_Get_Group_Negotiation_Members,
          Record<string, never>
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, { 
            communityId: params.communityId.toString(),
            negotiationId: params.negotiationId.toString()
          }),
          {}
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch group members.', { variant: 'error' });
        throw err;
      }
    },
  });
};